import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useToast } from '../hooks/use-toast';
import './Home.css';

const Reveal = ({ children, delay = 0, className = '', direction = 'up' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const variants = {
    up: { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } },
    left: { hidden: { opacity: 0, x: -32 }, visible: { opacity: 1, x: 0 } },
    right: { hidden: { opacity: 0, x: 32 }, visible: { opacity: 1, x: 0 } },
  };
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={variants[direction]}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Contact = () => {
  const { toast } = useToast();
  const [contactDone, setContactDone] = useState(false);

  const handleContact = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = {
      name: fd.get('name'),
      email: fd.get('email'),
      phone: '',
      userType: 'General',
      ventureInterest: fd.get('reason') || 'General',
      message: fd.get('message'),
      consent: true,
    };
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      setContactDone(true);
      e.target.reset();
    } catch {
      toast({ title: 'Could not send', description: 'Please try again or email hello@theprismaglobal.com', variant: 'destructive' });
    }
  };

  return (
    <div className="prisma-home" data-testid="contact-page">
      <SEO
        title="Contact | The Prisma Global"
        description="Reach out to The Prisma Global — collaborations, investments, press, and studio partnerships. Based in Solapur, Maharashtra. Working with people regardless of location."
        canonical="https://www.theprismaglobal.com/contact"
        keywords="contact The Prisma Global, Solapur Maharashtra business, Veda Lolge contact, Prisma collaboration, Prisma investment inquiries, Prisma press contact, hello@theprismaglobal.com"
      />
      <Navbar />

      {/* Page Hero (dark) */}
      <section className="contact-page-hero" data-testid="contact-page-hero">
        <div className="section-inner">
          <Reveal>
            <p className="section-label">Get in Touch</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="contact-page-title">
              Start a<br/>Conversation.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="contact-page-sub">
              We work with a small number of partners, investors, and collaborators at any given time. If the work feels aligned, we want to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Contact Form Section — same markup as homepage contact section */}
      <section className="prisma-contact section-light" id="contact" data-testid="contact-section">
        <div className="section-inner contact-grid">
          <Reveal direction="left">
            <div>
              <h2 className="contact-heading">How to Reach Us</h2>
              <p className="contact-sub">Choose the channel that fits — direct email, social, or the form. Most inquiries are answered within two working days.</p>
              <div className="contact-details">
                <p><span className="contact-detail-label">Email</span><a href="mailto:hello@theprismaglobal.com" data-testid="contact-email-link">hello@theprismaglobal.com</a></p>
                <p><span className="contact-detail-label">Based in</span>Solapur, Maharashtra</p>
                <div className="contact-social">
                  <a href="https://www.instagram.com/prisma.global5/" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram">Instagram</a>
                  <span className="contact-social-sep">&middot;</span>
                  <a href="https://www.linkedin.com/in/vedavati-lolge-58412862/" target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin">LinkedIn</a>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal direction="right" delay={0.1}>
            {contactDone ? (
              <p className="contact-success" data-testid="contact-success">Thank you. We&rsquo;ll be in touch.</p>
            ) : (
              <form className="contact-form" onSubmit={handleContact} data-testid="contact-form">
                <input name="name" placeholder="Name" required data-testid="contact-name" />
                <input name="email" type="email" placeholder="Email" required data-testid="contact-email" />
                <select name="reason" required defaultValue="" data-testid="contact-reason">
                  <option value="" disabled>Reason</option>
                  <option value="Collaboration">Collaboration</option>
                  <option value="Investment">Investment</option>
                  <option value="Press">Press</option>
                  <option value="Studio">Studio</option>
                  <option value="Other">Other</option>
                </select>
                <textarea name="message" rows="4" placeholder="Message" required data-testid="contact-message" />
                <button type="submit" className="cta-primary" data-testid="contact-submit">Send</button>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
